import { useCallback, useEffect, useState } from "react";
import { Loader2, UserPlus, Trash2, Smartphone, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "rounded-xl border border-emerald-950/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50";

const ROLE_STYLES = {
  admin: "bg-pine text-bone",
  crew: "bg-limed-light text-pine",
};

export default function UsersTab({ currentEmail }) {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "crew" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't load users."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success(
        `${form.name} added. Share the password with them — they log in at /admin and tap "Notify me" on their phone.`
      );
      setForm({ name: "", email: "", password: "", role: "crew" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeUser = async (user) => {
    if (!window.confirm(`Remove ${user.name} (${user.email})? Their devices stop getting notifications.`)) return;
    try {
      await api.delete(`/admin/users/${encodeURIComponent(user.email)}`);
      toast.success("User removed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!users) {
    return (
      <div className="flex justify-center py-20 text-pine">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-users-panel" className="space-y-6">
      <form
        onSubmit={addUser}
        className="bg-white rounded-3xl border border-emerald-950/10 p-6"
      >
        <div className="flex items-center gap-2.5 mb-1.5">
          <UserPlus size={17} className="text-limed" />
          <h3 className="font-display font-bold text-emerald-950">Add a crew member</h3>
        </div>
        <p className="text-sm text-emerald-900/60 mb-5">
          They can log in at /admin, see bookings and quotes, and get push notifications on their
          phone. Only admins can change services, pricing, availability, or users.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <input
            data-testid="user-add-name"
            required
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={`${inputCls} flex-1 min-w-[130px]`}
          />
          <input
            data-testid="user-add-email"
            required
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={`${inputCls} flex-1 min-w-[180px]`}
          />
          <input
            data-testid="user-add-password"
            required
            minLength={8}
            placeholder="Password (8+ chars)"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={`${inputCls} flex-1 min-w-[150px]`}
          />
          <select
            data-testid="user-add-role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className={`${inputCls} bg-white`}
          >
            <option value="crew">Crew member</option>
            <option value="admin">Admin</option>
          </select>
          <button
            data-testid="user-add-btn"
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-pine text-bone font-semibold px-6 py-2.5 rounded-full hover:bg-limed hover:text-pine-dark transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}
            Add User
          </button>
        </div>
      </form>

      <div className="space-y-3">
        {users.map((u) => (
          <div
            key={u.email}
            data-testid={`admin-user-row-${u.email}`}
            className="bg-white rounded-2xl border border-emerald-950/10 p-4 flex flex-wrap items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-sage flex items-center justify-center text-pine font-display font-bold">
              {(u.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-[160px]">
              <p className="font-semibold text-emerald-950 text-sm">
                {u.name}
                {u.email === currentEmail && (
                  <span className="ml-2 text-xs font-mono text-emerald-900/40">(you)</span>
                )}
              </p>
              <p className="text-xs text-emerald-900/50">{u.email}</p>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 text-[11px] font-mono font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full ${ROLE_STYLES[u.role] || ROLE_STYLES.crew}`}
            >
              {u.role === "admin" && <ShieldCheck size={11} />}
              {u.role}
            </span>
            <span
              data-testid={`admin-user-devices-${u.email}`}
              className="inline-flex items-center gap-1.5 text-xs text-emerald-900/50"
              title="Devices subscribed to booking notifications"
            >
              <Smartphone size={13} />
              {u.devices || 0} {u.devices === 1 ? "device" : "devices"}
            </span>
            {u.email !== currentEmail && (
              <button
                data-testid={`admin-user-delete-${u.email}`}
                onClick={() => removeUser(u)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold border border-emerald-950/15 text-emerald-900/60 px-4 py-2 rounded-full hover:border-red-300 hover:text-red-500 transition-colors"
              >
                <Trash2 size={13} /> Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
